import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent as ReactMouseEvent, RefObject } from 'react'
import { isLandingCoderTask } from '~/components/task/LandingCoderTaskPanel'
import { isLandingDataWeaveTask } from '~/components/task/LandingDataWeaveTaskPanel'
import { isLandingDocumentationTask } from '~/components/task/LandingDocumentationTaskPanel'
import { TaskStatuses } from '~/constants/task-status'
import { agentTaskStatusFromJSON } from '~/protos/agents'
import type { ConversationWithTasks } from '~/types/Conversations'
import type { TaskObjectWithNoDetails } from '~/types/TaskType'
import { getEffectiveTaskStatus, normalizeCommandType } from '~/task/commandTypeUtils'
import { sortTasksForDisplay } from '~/utils/taskSort'
import type { ChatMessage } from './chatTypes'
import { findTaskKickoffContent } from './ChatMessage'

export const LANDING_TASK_PANEL_DEFAULT_WIDTH = 560
export const LANDING_TASK_PANEL_MIN_WIDTH = 520
export const LANDING_TASK_PANEL_MAX_WIDTH = 1040
const LANDING_TASK_PANEL_MIN_CHAT_WIDTH = 520
const LANDING_TASK_PANEL_WIDTH_STORAGE_KEY = 'curie-landing-task-panel-width'

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function getStoredLandingTaskPanelWidth(): number {
  if (typeof window === 'undefined') return LANDING_TASK_PANEL_DEFAULT_WIDTH

  let parsed = NaN
  try {
    const stored = window.localStorage.getItem(LANDING_TASK_PANEL_WIDTH_STORAGE_KEY)
    parsed = stored ? Number(stored) : NaN
  } catch {
    return LANDING_TASK_PANEL_DEFAULT_WIDTH
  }

  if (!Number.isFinite(parsed)) return LANDING_TASK_PANEL_DEFAULT_WIDTH

  return clampNumber(parsed, LANDING_TASK_PANEL_MIN_WIDTH, LANDING_TASK_PANEL_MAX_WIDTH)
}

function getDisplayTaskStatus(task: TaskObjectWithNoDetails | undefined) {
  if (!task) return undefined
  const status = agentTaskStatusFromJSON(task.status)
  return getEffectiveTaskStatus(status, normalizeCommandType(task.cmd_type))
}

function getTaskStatusGroup(task: TaskObjectWithNoDetails | undefined) {
  const status = getDisplayTaskStatus(task)
  if (status === undefined) return 'Task'
  return TaskStatuses[status]?.group
}

function isTaskRunning(task: TaskObjectWithNoDetails | undefined): boolean {
  return getTaskStatusGroup(task) === 'IN_PROGRESS'
}

function isTaskActionableOrDone(task: TaskObjectWithNoDetails | undefined): boolean {
  const group = getTaskStatusGroup(task)
  return (
    group === 'NEED_ACTION' || group === 'COMPLETED' || group === 'ERRORED' || group === 'STOPPED'
  )
}

interface UseLandingArtifactPanelOptions {
  chatLayoutRef: RefObject<HTMLDivElement>
  conversation: ConversationWithTasks | null | undefined
  conversationUuid: string | undefined
  editableCanvasOpen: boolean
  handleTaskLinkClick: (taskId: number) => void
  isPlugin: boolean
  processedMessages: ChatMessage[]
  taskPanelLayoutRef: RefObject<HTMLDivElement>
}

export function useLandingArtifactPanel({
  chatLayoutRef,
  conversation,
  conversationUuid,
  editableCanvasOpen,
  handleTaskLinkClick,
  isPlugin,
  processedMessages,
  taskPanelLayoutRef
}: UseLandingArtifactPanelOptions) {
  const lastAutoOpenedTaskPanelKeyRef = useRef<string | null>(null)
  const taskPanelManualSelectionRef = useRef(false)
  const taskPanelResizeCleanupRef = useRef<(() => void) | null>(null)
  const isMountedRef = useRef(true)
  const [selectedTaskPanelTaskId, setSelectedTaskPanelTaskId] = useState<number | null>(null)
  const [isTaskPanelMinimized, setIsTaskPanelMinimized] = useState(false)
  const [pendingArtifactTaskId, setPendingArtifactTaskId] = useState<number | null>(null)
  const [isTaskListOpen, setIsTaskListOpen] = useState(false)
  const [taskPanelWidthPx, setTaskPanelWidthPx] = useState(getStoredLandingTaskPanelWidth)
  const [isTaskPanelResizing, setIsTaskPanelResizing] = useState(false)
  const [taskPanelSelectionVersion, setTaskPanelSelectionVersion] = useState(0)

  const sortedConversationTasks = useMemo(
    () => (conversation?.tasks ? sortTasksForDisplay(conversation.tasks) : []),
    [conversation?.tasks]
  )

  const supportsLandingArtifactPanel = useCallback(
    (task?: (typeof sortedConversationTasks)[number]) =>
      !!task &&
      (isLandingCoderTask(task) ||
        isLandingDataWeaveTask(task) ||
        isLandingDocumentationTask(task)),
    []
  )

  const activeRunningLandingArtifactTask = useMemo(
    () =>
      sortedConversationTasks.find(
        task => supportsLandingArtifactPanel(task) && isTaskRunning(task)
      ),
    [sortedConversationTasks, supportsLandingArtifactPanel]
  )

  const landingArtifactTasks = useMemo(
    () => sortedConversationTasks.filter(task => supportsLandingArtifactPanel(task)),
    [sortedConversationTasks, supportsLandingArtifactPanel]
  )

  const latestLandingArtifactTask = useMemo(
    () =>
      activeRunningLandingArtifactTask ??
      landingArtifactTasks.find(task => isTaskActionableOrDone(task)) ??
      landingArtifactTasks[0],
    [activeRunningLandingArtifactTask, landingArtifactTasks]
  )

  useEffect(() => {
    lastAutoOpenedTaskPanelKeyRef.current = null
    taskPanelManualSelectionRef.current = false
    setSelectedTaskPanelTaskId(null)
    setIsTaskPanelMinimized(false)
    setPendingArtifactTaskId(null)
    setIsTaskListOpen(false)
    setTaskPanelSelectionVersion(0)
  }, [conversationUuid])

  const latestLandingArtifactTaskId = latestLandingArtifactTask?.task_id ?? null
  const latestLandingArtifactTaskAutoKey = latestLandingArtifactTask
    ? `${latestLandingArtifactTask.task_id}:${latestLandingArtifactTask.status}`
    : null

  useEffect(() => {
    if (!latestLandingArtifactTaskId || !latestLandingArtifactTaskAutoKey) {
      return
    }
    if (lastAutoOpenedTaskPanelKeyRef.current === latestLandingArtifactTaskAutoKey) {
      return
    }

    const isViewingDifferentManualTask =
      taskPanelManualSelectionRef.current &&
      selectedTaskPanelTaskId !== null &&
      selectedTaskPanelTaskId !== latestLandingArtifactTaskId

    if (isViewingDifferentManualTask) {
      setPendingArtifactTaskId(latestLandingArtifactTaskId)
      return
    }

    const isAutoSelectingDifferentTask = selectedTaskPanelTaskId !== latestLandingArtifactTaskId

    lastAutoOpenedTaskPanelKeyRef.current = latestLandingArtifactTaskAutoKey
    setSelectedTaskPanelTaskId(latestLandingArtifactTaskId)
    if (isAutoSelectingDifferentTask) {
      setIsTaskPanelMinimized(false)
    }
    setPendingArtifactTaskId(null)
  }, [latestLandingArtifactTaskAutoKey, latestLandingArtifactTaskId, selectedTaskPanelTaskId])

  const activeLandingArtifactTask = useMemo(() => {
    if (selectedTaskPanelTaskId !== null) {
      const selectedTask = sortedConversationTasks.find(
        task => task.task_id === selectedTaskPanelTaskId
      )
      if (supportsLandingArtifactPanel(selectedTask)) {
        return selectedTask
      }
    }
    return latestLandingArtifactTask
  }, [
    latestLandingArtifactTask,
    selectedTaskPanelTaskId,
    sortedConversationTasks,
    supportsLandingArtifactPanel
  ])

  const activeLandingArtifactTaskKickoffContent = useMemo(
    () => findTaskKickoffContent(processedMessages, activeLandingArtifactTask?.task_id),
    [activeLandingArtifactTask?.task_id, processedMessages]
  )

  const selectLandingArtifactTask = useCallback(
    (taskId: number) => {
      const task = sortedConversationTasks.find(candidate => candidate.task_id === taskId)
      if (!supportsLandingArtifactPanel(task)) {
        handleTaskLinkClick(taskId)
        return
      }

      taskPanelManualSelectionRef.current =
        latestLandingArtifactTaskId !== null && taskId !== latestLandingArtifactTaskId
      setSelectedTaskPanelTaskId(taskId)
      setIsTaskPanelMinimized(false)
      setTaskPanelSelectionVersion(previous => previous + 1)

      if (taskId === pendingArtifactTaskId) {
        setPendingArtifactTaskId(null)
      }

      if (taskId === latestLandingArtifactTaskId && latestLandingArtifactTaskAutoKey) {
        lastAutoOpenedTaskPanelKeyRef.current = latestLandingArtifactTaskAutoKey
      }
    },
    [
      handleTaskLinkClick,
      latestLandingArtifactTaskAutoKey,
      latestLandingArtifactTaskId,
      pendingArtifactTaskId,
      sortedConversationTasks,
      supportsLandingArtifactPanel
    ]
  )

  const handleTaskPanelOpen = useCallback(
    (taskId: number) => {
      selectLandingArtifactTask(taskId)
    },
    [selectLandingArtifactTask]
  )

  const handleTaskListSelect = useCallback(
    (taskId: number) => {
      setIsTaskListOpen(false)
      selectLandingArtifactTask(taskId)
    },
    [selectLandingArtifactTask]
  )

  const canShowRightPanel =
    !isPlugin && !!conversation && conversation.tasks.length > 0 && !editableCanvasOpen
  const showArtifactPanel = canShowRightPanel && !!activeLandingArtifactTask && !!conversationUuid
  const shouldRenderRightPanel = canShowRightPanel && (!showArtifactPanel || !isTaskPanelMinimized)
  const shouldRenderArtifactPanel = showArtifactPanel && !isTaskPanelMinimized

  const getTaskPanelWidthBounds = useCallback(() => {
    const rect =
      taskPanelLayoutRef.current?.getBoundingClientRect() ??
      chatLayoutRef.current?.getBoundingClientRect()
    const availableWidth =
      rect && rect.width > 0
        ? rect.width
        : typeof window !== 'undefined'
          ? window.innerWidth
          : LANDING_TASK_PANEL_DEFAULT_WIDTH + LANDING_TASK_PANEL_MIN_CHAT_WIDTH
    const maxWidth = Math.max(
      LANDING_TASK_PANEL_MIN_WIDTH,
      Math.min(
        LANDING_TASK_PANEL_MAX_WIDTH,
        Math.floor(availableWidth - LANDING_TASK_PANEL_MIN_CHAT_WIDTH)
      )
    )

    return {
      min: LANDING_TASK_PANEL_MIN_WIDTH,
      max: maxWidth
    }
  }, [chatLayoutRef, taskPanelLayoutRef])

  const clampTaskPanelWidth = useCallback(
    (width: number) => {
      const bounds = getTaskPanelWidthBounds()
      return clampNumber(Math.round(width), bounds.min, bounds.max)
    },
    [getTaskPanelWidthBounds]
  )

  const persistTaskPanelWidth = useCallback((width: number) => {
    try {
      window.localStorage.setItem(LANDING_TASK_PANEL_WIDTH_STORAGE_KEY, String(width))
    } catch {
      // Ignore storage failures; resizing should still work for the current session.
    }
  }, [])

  const updateTaskPanelWidth = useCallback(
    (width: number) => {
      setTaskPanelWidthPx(() => {
        const nextWidth = clampTaskPanelWidth(width)
        persistTaskPanelWidth(nextWidth)
        return nextWidth
      })
    },
    [clampTaskPanelWidth, persistTaskPanelWidth]
  )

  const handleTaskPanelResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!shouldRenderArtifactPanel) return

      event.preventDefault()
      taskPanelResizeCleanupRef.current?.()

      const previousCursor = document.body.style.cursor
      const previousUserSelect = document.body.style.userSelect
      let latestWidth = taskPanelWidthPx

      const getContainerRight = () => {
        const rect =
          taskPanelLayoutRef.current?.getBoundingClientRect() ??
          chatLayoutRef.current?.getBoundingClientRect()
        if (rect && rect.width > 0) return rect.right
        return window.innerWidth
      }

      const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
        moveEvent.preventDefault()
        if (!Number.isFinite(moveEvent.clientX)) return

        latestWidth = clampTaskPanelWidth(getContainerRight() - moveEvent.clientX)
        setTaskPanelWidthPx(latestWidth)
      }

      const cleanup = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', cleanup)
        window.removeEventListener('blur', cleanup)
        document.body.style.cursor = previousCursor
        document.body.style.userSelect = previousUserSelect
        if (isMountedRef.current) {
          setIsTaskPanelResizing(false)
        }
        persistTaskPanelWidth(latestWidth)
        taskPanelResizeCleanupRef.current = null
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      setIsTaskPanelResizing(true)
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', cleanup)
      window.addEventListener('blur', cleanup)
      taskPanelResizeCleanupRef.current = cleanup
    },
    [
      chatLayoutRef,
      clampTaskPanelWidth,
      persistTaskPanelWidth,
      shouldRenderArtifactPanel,
      taskPanelLayoutRef,
      taskPanelWidthPx
    ]
  )

  const handleTaskPanelResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!shouldRenderArtifactPanel) return

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        updateTaskPanelWidth(taskPanelWidthPx + 40)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        updateTaskPanelWidth(taskPanelWidthPx - 40)
      } else if (event.key === 'Home') {
        event.preventDefault()
        updateTaskPanelWidth(LANDING_TASK_PANEL_MIN_WIDTH)
      } else if (event.key === 'End') {
        event.preventDefault()
        updateTaskPanelWidth(LANDING_TASK_PANEL_MAX_WIDTH)
      }
    },
    [shouldRenderArtifactPanel, taskPanelWidthPx, updateTaskPanelWidth]
  )

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      taskPanelResizeCleanupRef.current?.()
    }
  }, [])

  useEffect(() => {
    if (!shouldRenderArtifactPanel) return

    const handleResize = () => {
      setTaskPanelWidthPx(width => {
        const nextWidth = clampTaskPanelWidth(width)
        if (nextWidth !== width) {
          persistTaskPanelWidth(nextWidth)
        }
        return nextWidth
      })
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [clampTaskPanelWidth, persistTaskPanelWidth, shouldRenderArtifactPanel])

  const showCoderPanel =
    shouldRenderArtifactPanel &&
    !!activeLandingArtifactTask &&
    isLandingCoderTask(activeLandingArtifactTask)
  const showDocumentationPanel =
    shouldRenderArtifactPanel &&
    !!activeLandingArtifactTask &&
    isLandingDocumentationTask(activeLandingArtifactTask)
  const showDataWeavePanel =
    shouldRenderArtifactPanel &&
    !!activeLandingArtifactTask &&
    isLandingDataWeaveTask(activeLandingArtifactTask)

  return {
    activeLandingArtifactTask,
    activeLandingArtifactTaskKickoffContent,
    activeRunningLandingArtifactTask,
    canShowRightPanel,
    getTaskPanelWidthBounds,
    handleTaskListSelect,
    handleTaskPanelOpen,
    handleTaskPanelResizeKeyDown,
    handleTaskPanelResizeStart,
    isTaskListOpen,
    isTaskPanelResizing,
    rightPanelWidthPx: showArtifactPanel ? taskPanelWidthPx : 300,
    setIsTaskListOpen,
    setIsTaskPanelMinimized,
    shouldRenderArtifactPanel,
    shouldRenderRightPanel,
    showCoderPanel,
    showDataWeavePanel,
    showDocumentationPanel,
    sortedConversationTasks,
    taskPanelSelectionVersion,
    useDesktopSplitPaneLayout: !isPlugin && shouldRenderRightPanel
  }
}
